// react_frontend/src/Components/Modules/ItemSetting/EditItem.js 
import React, { useState, useRef, useContext, useEffect } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import {
  Form,
  Button,
  Message,
  Loader,
  Whisper,
  Tooltip,
  Schema,
  SelectPicker,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { FaBox, FaTags, FaClipboardList, FaPencilAlt } from "react-icons/fa";

const EditItem = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    updateItemInList,
    categories,
    items,
    startCategoryPolling,
    stopCategoryPolling,
    startItemPolling,
    stopItemPolling,
  } = useContext(MotherContext);

  // ✅ Start polling when component mounts
  useEffect(() => {
    startCategoryPolling();
    startItemPolling();
    return () => {
      stopCategoryPolling();
      stopItemPolling();
    };
  }, [startCategoryPolling, stopCategoryPolling, startItemPolling, stopItemPolling]);

  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // ✅ Default units (same as AddItem)
  const defaultUnitData = [
    { label: "Piece (Pcs)", value: "Pcs" },
    { label: "Set", value: "Set" },
    { label: "Dozen", value: "Dozen" },
    { label: "Packet", value: "Packet" },
    { label: "Box", value: "Box" },
    { label: "Carton", value: "Carton" },
    { label: "Bag", value: "Bag" },
    { label: "Roll", value: "Roll" },
    { label: "Bundle", value: "Bundle" },
    { label: "Unit", value: "Unit" },
    { label: "Plate", value: "Plate" },
    { label: "Portion", value: "Portion" },
    { label: "Cup", value: "Cup" },
    { label: "Bottle", value: "Bottle" },
    { label: "Glass", value: "Glass" },
    { label: "Slice", value: "Slice" },
    { label: "Bowl", value: "Bowl" },
    { label: "Tablet", value: "Tablet" },
    { label: "Capsule", value: "Capsule" },
    { label: "Strip", value: "Strip" },
    { label: "Tube", value: "Tube" },
    { label: "Drop", value: "Drop" },
    { label: "Ampoule", value: "Ampoule" },
    { label: "Vial", value: "Vial" },
    { label: "Jar", value: "Jar" },
    { label: "Gram (g)", value: "Gram" },
    { label: "Kilogram (kg)", value: "Kilogram" },
    { label: "Milligram (mg)", value: "Milligram" },
    { label: "Tonne (ton)", value: "Tonne" },
    { label: "Pound (lb)", value: "Pound" },
    { label: "Ounce (oz)", value: "Ounce" },
    { label: "Liter (L)", value: "Liter" },
    { label: "Milliliter (mL)", value: "Milliliter" },
    { label: "Gallon", value: "Gallon" },
    { label: "Pint", value: "Pint" },
    { label: "Quart", value: "Quart" },
    { label: "Barrel", value: "Barrel" },
    { label: "Sheet", value: "Sheet" },
    { label: "Coil", value: "Coil" },
    { label: "Rod", value: "Rod" },
    { label: "Bar", value: "Bar" },
    { label: "Square Meter (m²)", value: "Square Meter" },
    { label: "Square Foot (ft²)", value: "Square Foot" },
    { label: "N/A", value: "N/A" },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const [unitOptions, setUnitOptions] = useState(defaultUnitData);

  // ✅ Build unit list from MotherContext's `items`
  useEffect(() => {
    if (items.length > 0) {
      const uniqueUnits = [
        ...new Set(
          items
            .map(i => (i.product_unit || "").trim())
            .filter(unit => unit.length > 0)
        )
      ];

      const seen = new Set(defaultUnitData.map(u => u.value.toLowerCase()));
      const dbUnits = [];

      uniqueUnits.forEach(unit => {
        const lower = unit.toLowerCase();
        if (!seen.has(lower)) {
          seen.add(lower);
          dbUnits.push({ label: unit, value: unit });
        }
      });

      dbUnits.sort((a, b) => a.label.localeCompare(b.label));
      setUnitOptions([...defaultUnitData, ...dbUnits]);
    }
  }, [items]);

  // ✅ Find and load the item to edit
  useEffect(() => {
    if (items.length > 0) {
      const item = items.find(i => i.id == id);
      if (item) {
        setFormValue({
          category_id: item.category_id?.toString(),
          item_name: item.item_name,
          product_unit: (item.product_unit || "").trim(),
        });
      } else {
        // If not found in context, fetch directly (fallback)
        fetchItemDirectly();
      }
    } else {
      fetchItemDirectly();
    }
  }, [items, id]);

  const fetchItemDirectly = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get(`add_item/${id}`);
      const item = res.data.item;
      if (item) {
        setFormValue({
          category_id: item.category_id?.toString(),
          item_name: item.item_name,
          product_unit: (item.product_unit || "").trim(),
        });
      } else {
        throw new Error("Not found");
      }
    } catch (err) {
      console.error("Failed to fetch item directly", err);
      toaster.push(
        <Message type="error" duration={5000}>
          Failed to load item.
        </Message>,
        { placement: "topEnd" }
      );
      navigate("/item_list");
    } finally {
      setLoading(false);
    }
  };

  const activeCategories = categories.filter(cat => cat.category_status === 1);
  const selectedItemCategory = categories.find(cat => cat.id.toString() === formValue.category_id);
  const showInactiveWarning = selectedItemCategory && selectedItemCategory.category_status !== 1;

  const { StringType } = Schema.Types;
  const model = Schema.Model({
    category_id: StringType().isRequired("Category is required."),
    item_name: StringType().isRequired("Item name is required."),
    product_unit: StringType().isRequired("Unit is required."),
  });

  const handleSubmit = async () => {
    if (!formRef.current.check()) return;
    setLoading(true);
    try {
      const payload = {
        category_id: formValue.category_id,
        item_name: formValue.item_name,
        product_unit: formValue.product_unit,
      };

      const res = await AxiosAuthInstance.put(`add_item/${id}`, payload);

      if (res.data.status === true) {
        toaster.push(
          <Message type="success" duration={3000}>
            Item updated successfully!
          </Message>,
          { placement: "topEnd" }
        );
        if (res.data.item) {
          updateItemInList(res.data.item);
        }
        navigate("/item_list");
      } else {
        toaster.push(
          <Message type="error" duration={5000}>
            Update failed: {res.data.message || "Unknown error"}
          </Message>,
          { placement: "topEnd" }
        );
      }
    } catch (err) {
      console.error("Update error:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        "Failed to update item.";
      toaster.push(
        <Message type="error" duration={5000}>
          {errorMsg}
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/item_list");
  };

  return (
    <>
      <Breadcrumb title="Edit Item" />

      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPencilAlt /> Edit Item
            </h5>
            <Link to="/item_list" className="text-white text-decoration-none">
              <Whisper placement="left" speaker={<Tooltip>Item List</Tooltip>}>
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>

          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
              model={model}
            >
              {/* Category */}
              <Form.Group controlId="category_id">
                <Form.ControlLabel>Category *</Form.ControlLabel>
                <Form.Control
                  name="category_id"
                  accepter={SelectPicker}
                  data={[
                    ...(showInactiveWarning
                      ? [
                        {
                          label: (
                            <div className="d-flex align-items-center gap-2">
                              <FaTags className="text-muted" />
                              <span className="text-muted">
                                {selectedItemCategory.category_name} (Inactive)
                              </span>
                            </div>
                          ),
                          value: selectedItemCategory.id.toString(),
                          disabled: true,
                        },
                      ]
                      : []),
                    ...activeCategories.map((cat) => ({
                      label: (
                        <div className="d-flex align-items-center gap-2">
                          <FaTags /> <span>{cat.category_name}</span>
                        </div>
                      ),
                      value: cat.id.toString(),
                    })),
                  ]}
                  placeholder="Select Category"
                  style={{ width: "100%" }}
                  renderMenuOnBody
                  cleanable={false}
                />
                {showInactiveWarning && (
                  <Form.HelpText className="text-warning mt-1">
                    ⚠️ This item belongs to an inactive category. Please select
                    an active category to update.
                  </Form.HelpText>
                )}
                <Form.ErrorMessage name="category_id" />
              </Form.Group>

              {/* Item Name */}
              <Form.Group controlId="item_name">
                <Form.ControlLabel>Item Name *</Form.ControlLabel>
                <Form.Control
                  name="item_name"
                  placeholder="e.g., iPhone 15"
                  prefix={<FaBox />}
                />
                <Form.ErrorMessage name="item_name" />
              </Form.Group>

              {/* Product Unit */}
              <Form.Group controlId="product_unit">
                <Form.ControlLabel>Unit *</Form.ControlLabel>
                <Form.Control
                  name="product_unit"
                  accepter={SelectPicker}
                  data={unitOptions}
                  placeholder="Select Unit"
                  style={{ width: "100%" }}
                  renderMenuOnBody
                  cleanable={false}
                />
                <Form.ErrorMessage name="product_unit" />
              </Form.Group>

              {/* Action Buttons */}
              <div
                className="d-flex gap-3 pt-3 border-top"
                style={{ marginTop: "2rem" }}
              >
                <Button
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Updating...
                    </>
                  ) : (
                    "Update Item"
                  )}
                </Button>

                <Button
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{
                    flex: 1,
                    border: "1px solid #dc3545",
                    color: "#dc3545",
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditItem;